import {
  type JSX,
  For,
  createSignal,
  Switch,
  Match,
  createMemo,
} from 'solid-js';
import { createVirtualizer } from '@tanstack/solid-virtual';
import { translate } from '@fulcrum/lib';
import { IssueListProps } from '../../types/issue.types';

import { IssueResponse } from '@data-access/connect/tc-issue-management';
import { IssueCard, IssueCardSkeleton } from '../../components';

// Constants for virtualized list configuration
const ISSUE_CARD_ESTIMATED_HEIGHT = 169;
const VIRTUAL_LIST_OVERSCAN = 2;
const SKELETON_LOADING_COUNT = 8;
const Z_INDEX_BASE = 50;
const Z_INDEX_MIN = 1;

/**
 * Transform API response to UI data format
 * Maps the API response fields to the expected UI format
 * Includes validation and error handling for malformed data
 */
function transformIssueResponse(apiIssue: IssueResponse): IssueResponse {
  // Validate required fields
  if (!apiIssue || typeof apiIssue !== 'object') {
    throw new Error('Invalid issue data: expected an object');
  }

  if (!apiIssue.serverAssignedId || !apiIssue.title) {
    console.warn('Issue missing required fields:', {
      id: apiIssue.serverAssignedId,
      title: apiIssue.title,
      description: apiIssue.description,
      hasServerAssignedId: !!apiIssue.serverAssignedId,
      hasTitle: !!apiIssue.title,
    });
  }

  return {
    id: apiIssue.id || `temp-${Date.now()}`,
    title: apiIssue.title || 'Untitled Issue',
    description: apiIssue.description || '',
    type: apiIssue.type,
    priority: apiIssue.priority,
    status: apiIssue.status,
    assignees: Array.isArray(apiIssue.assignees) ? apiIssue.assignees : [],
    dueDate: apiIssue.dueDate || '',
    createdBy: apiIssue.createdBy || '',
    createdAt: apiIssue.createdAt || new Date().toISOString(),
    updatedBy: apiIssue.updatedBy || '',
    updatedAt: apiIssue.updatedAt || new Date().toISOString(),
    contextId: apiIssue.contextId || '',
    serverAssignedId: apiIssue.serverAssignedId || `temp-${Date.now()}`,
    marker: apiIssue.marker,
    _actions: apiIssue._actions,
  };
}

export function IssueList(props: Readonly<IssueListProps>): JSX.Element {
  // Transform API data to UI format with error handling
  const transformedData = createMemo(() => {
    try {
      const apiData = props.issuesData?.items;

      // Validate data structure
      if (!apiData) {
        return [];
      }

      if (!Array.isArray(apiData)) {
        console.warn(
          'Expected issuesData.items to be an array, received:',
          typeof apiData
        );
        return [];
      }

      // Transform each item with error handling
      return apiData
        .map((item: IssueResponse, index: number) => {
          try {
            return transformIssueResponse(item);
          } catch (error) {
            console.error(
              `Failed to transform issue at index ${index}:`,
              error,
              item
            );
            // Return a fallback issue instead of crashing
            return {
              id: `error-${index}`,
              title: 'Error loading issue',
              description: 'This issue could not be loaded properly',
              type: 'Issue',
              priority: 'Normal',
              assignees: [],
              dueDate: '',
              createdBy: '',
              createdAt: new Date().toISOString(),
              updatedBy: '',
              updatedAt: new Date().toISOString(),
              contextId: '',
              serverAssignedId: `error-${index}`,
              marker: undefined,
              _actions: undefined,
              objectId: `error-${index}`,
            } as IssueResponse;
          }
        })
        .filter(Boolean); // Remove any null/undefined items
    } catch (error) {
      console.error('Failed to transform issues data:', error);
      return [];
    }
  });

  // Virtualized list management
  const [parentRef, setParentRef] = createSignal<HTMLDivElement | null>(null);

  // Check if we should show loading state (initial state or explicit loading)
  const isLoadingState = (): boolean => {
    // Show loading if explicitly loading OR if we have no data yet
    const isExplicitlyLoading = props.isLoading;
    return isExplicitlyLoading;
  };

  // Create the virtualizer - always create, but defer scroll element
  const virtualizer = createMemo(() => {
    const parent = parentRef();
    const loading = isLoadingState();
    const count = loading ? SKELETON_LOADING_COUNT : transformedData().length;

    return createVirtualizer({
      get count() {
        return count;
      },
      getScrollElement: () => parent || document.createElement('div'), // Fallback element
      estimateSize: () => ISSUE_CARD_ESTIMATED_HEIGHT, // Estimated height for each IssueCard
      overscan: VIRTUAL_LIST_OVERSCAN, // Render extra items outside viewport for smooth scrolling
    });
  });

  const handleRowClick = (issue: IssueResponse): void => {
    props.onRowClick?.(issue);
  };

  // Render functions for different states
  const renderErrorState = (errorInfo?: {
    title?: string;
    message?: string;
    icon?: string;
  }): JSX.Element => (
    <div class="flex-1 flex items-center justify-center">
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore - tc-empty is a custom web component not recognized by TypeScript */}
      <tc-empty
        title={errorInfo?.title || 'Error'}
        message={errorInfo?.message || 'An error occurred'}
        icon={errorInfo?.icon || 'icon-font tc-icon-warning'}
      />
    </div>
  );

  const renderEmptyState = (): JSX.Element => (
    <div class="flex-1 flex items-center justify-center">
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore - tc-empty is a custom web component not recognized by TypeScript */}
      <tc-empty
        title={translate('TC.Topic.NoTopics')}
        icon="icon-font tc-icon-bcf"
        message={translate('TC.Topic.EmptyTopicsMsg')}
      />
    </div>
  );

  const renderFallbackSkeletons = (): JSX.Element => (
    <div class="w-full h-full font-sans flex flex-col">
      <div class="flex-1 overflow-y-auto overflow-x-hidden">
        <div class="space-y-2">
          <For each={Array.from({ length: 8 }, (_, i) => i)}>
            {(_, index) => {
              return (
                <div
                  class="relative h-[169px]"
                  style={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- z-index is a CSS property name
                    'z-index': Math.max(Z_INDEX_MIN, Z_INDEX_BASE - index()),
                  }}
                >
                  <IssueCardSkeleton />
                </div>
              );
            }}
          </For>
        </div>
      </div>
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- VirtualItem type from @tanstack/solid-virtual
  const renderVirtualItem = (virtualItem: any): JSX.Element => {
    // Validate virtual item
    if (!virtualItem || typeof virtualItem.index !== 'number') {
      console.warn('Invalid virtual item:', virtualItem);
      return <IssueCardSkeleton />;
    }

    // Higher z-index for items that appear earlier (at the top) so their dropdowns appear above items below
    // Keep z-index in range 1-50 to stay below header/panel components (typically 100+)

    const zIndex = Math.max(Z_INDEX_MIN, Z_INDEX_BASE - virtualItem.index);

    return (
      <div
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: `${virtualItem.size || ISSUE_CARD_ESTIMATED_HEIGHT}px`,
          transform: `translateY(${virtualItem.start || 0}px)`,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- z-index is a CSS property name
          'z-index': `${zIndex}`,
        }}
        class="relative"
      >
        <Switch>
          <Match when={isLoadingState()}>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {(() => (
              <div class="w-full h-full">
                <IssueCardSkeleton />
              </div>
            ))()}
          </Match>
          <Match when={!isLoadingState()}>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {(() => {
              try {
                const issues = transformedData();
                const issue = issues[virtualItem.index];

                // Additional bounds checking
                if (!issue || virtualItem.index >= issues.length) {
                  console.warn(
                    `Issue not found at index ${virtualItem.index}, total issues: ${issues.length}`
                  );
                  return <IssueCardSkeleton />;
                }

                return (
                  <IssueCard
                    issue={issue}
                    onRowClick={handleRowClick}
                    projectUsersData={props.projectUsersData}
                    configurationData={props.configurationData}
                    iconsList={props.iconsList}
                    projectId={props.projectId}
                    contextId={props.contextId}
                    accessToken={props.accessToken}
                  />
                );
              } catch (error) {
                console.error(
                  `Error rendering issue at index ${virtualItem.index}:`,
                  error
                );
                return <IssueCardSkeleton />;
              }
            })()}
          </Match>
        </Switch>
      </div>
    );
  };

  return (
    <div class="w-full h-full font-sans flex flex-col">
      {/* Project tags are available via projectTags() for filtering/display */}

      {/* Content Section */}
      <Switch>
        <Match when={props.hasError}>
          {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
          {renderErrorState(props.errorInfo || undefined)}
        </Match>
        <Match when={!props.hasError}>
          {/* Issue Cards - Virtualized */}
          <div
            ref={setParentRef}
            class="flex-1 overflow-y-auto overflow-x-hidden"
          >
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {(() => {
              const v = virtualizer();
              const parent = parentRef();
              const loading = isLoadingState();
              const dataLength = transformedData().length;

              if (!loading && dataLength === 0) {
                return renderEmptyState();
              }

              if (loading) {
                return renderFallbackSkeletons();
              }

              if (!v || !parent) {
                // Show skeleton cards if virtualizer isn't ready yet
                return renderFallbackSkeletons();
              }

              const virtualItems = v.getVirtualItems();

              return (
                <div
                  style={{
                    height: `${v.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <For each={virtualItems}>
                    {(virtualItem) => renderVirtualItem(virtualItem)}
                  </For>
                </div>
              );
            })()}
          </div>
        </Match>
      </Switch>
    </div>
  );
}
